interface OccurrencesPage {
  docs: any[]
  hasNextPage: boolean
}

const PAGE_LIMIT = 100

// Same pattern as pages/people.vue: page 1 is fetched during SSR so the first
// screen renders immediately, and the remaining pages are fetched on the client
// after mount and appended as they arrive. This avoids both the silent
// truncation of a single hard-capped query and blocking SSR on a large archive.
export const usePaginatedOccurrences = async (
  key: string,
  query: string,
  variables: Record<string, unknown>,
) => {
  const fetchPage = (page: number) => $fetch<OccurrencesPage>('/api/payload-gql', {
    method: 'POST',
    body: {
      query,
      variables: { ...variables, page, limit: PAGE_LIMIT },
    },
  })

  const { data: firstPage } = await useAsyncData<OccurrencesPage>(
    key,
    () => fetchPage(1)
  )

  const extraDocs = ref<any[]>([])
  // An occurrence can stay published after its programme was unpublished (the
  // CMS sync hook only wrote a draft version), so also check the programme's
  // own status rather than trusting the occurrence's.
  const docs = computed(() => [
    ...(firstPage.value?.docs ?? []),
    ...extraDocs.value,
  ].filter(o => o.programme?._status === 'published'))

  // Not onMounted: after the await above there is no active component instance
  // inside a composable, so a lifecycle hook registered here would never fire.
  // Kick the loop off directly on the client instead; extraDocs is still empty
  // during hydration, so the server and client markup match.
  // True until every page is in, so callers can hold back the list and show a
  // loading state instead of a partial archive. Same value on server and client
  // for hydration; the client flips it once the loop finishes.
  const loading = ref(Boolean(firstPage.value?.hasNextPage))

  const loadRemainingPages = async () => {
    let page = 2
    while (true) {
      const result = await fetchPage(page)
      extraDocs.value = [...extraDocs.value, ...(result?.docs ?? [])]
      if (!result?.hasNextPage) break
      page++
    }
    loading.value = false
  }
  if (import.meta.client && loading.value) {
    loadRemainingPages()
  }

  return { docs, loading }
}
